import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Layers,
  Search,
  BookOpen,
  Package,
  ArrowRight,
  AlertTriangle,
  Check,
  Image as ImageIcon,
  FileText,
  Clock,
  User,
} from 'lucide-react';
import { api } from '@/lib/apiClient';
import { PlybookLaminationView } from '@/components/Plybook/PlybookLaminationView';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface WorkOrderInfo {
  id: string;
  order_number: string;
  product_name: string;
  customer?: string;
  plybook_id?: string;
  plybook?: {
    id: string;
    name: string;
    description?: string;
    total_plies: number;
    main_image_url?: string;
  } | null;
  linked_plotter_cut?: {
    id: string;
    package_code: string;
    package_name: string;
    total_layers: number;
    materials: any[];
  } | null;
  mold?: {
    id: string;
    name: string;
  } | null;
}

interface PlybookOption {
  id: string;
  name: string;
  description?: string;
  total_plies: number;
  main_image_url?: string;
  product_model?: string;
  created_at: string;
}

interface LaminationPlybookIntegrationProps {
  workOrderId: string;
  onBack?: () => void;
  onComplete?: () => void;
}

export function LaminationPlybookIntegration({
  workOrderId,
  onBack,
  onComplete,
}: LaminationPlybookIntegrationProps) {
  const [showPlybookSelector, setShowPlybookSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlybook, setSelectedPlybook] = useState<PlybookOption | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [showLaminationView, setShowLaminationView] = useState(false);

  // Fetch work order with plybook and plotter cut info
  const { data: workOrder, isLoading: woLoading, refetch: refetchWO } = useQuery({
    queryKey: ['lamination-work-order', workOrderId],
    queryFn: async () => {
      const { data, error } = await api
        .from('work_orders')
        .select(`
          id,
          order_number,
          product_name,
          customer,
          plybook_id,
          plybook:plybooks(id, name, description, total_plies, main_image_url),
          linked_plotter_cut_id,
          linked_plotter_cut:plotter_cuts(id, package_code, package_name, total_layers, materials),
          mold_id,
          mold:molds(id, name)
        `)
        .eq('id', workOrderId)
        .single();

      if (error) throw error;
      return data as WorkOrderInfo;
    },
  });

  // Fetch available plybooks for selection
  const { data: availablePlybooks = [], isLoading: plybooksLoading } = useQuery({
    queryKey: ['available-plybooks', searchTerm],
    queryFn: async () => {
      let query = api
        .from('plybooks')
        .select('id, name, description, total_plies, main_image_url, created_at')
        .eq('is_active', true)
        .order('name');

      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.limit(20);
      if (error) throw error;
      return data as PlybookOption[];
    },
    enabled: showPlybookSelector,
  });

  // Auto-show lamination view if plybook is already assigned
  useEffect(() => {
    if (workOrder?.plybook_id && workOrder?.plybook) {
      setShowLaminationView(true);
    }
  }, [workOrder]);

  const handleLinkPlybook = async (plybook: PlybookOption) => {
    setIsLinking(true);
    try {
      const { error } = await api
        .from('work_orders')
        .update({ plybook_id: plybook.id })
        .eq('id', workOrderId);

      if (error) throw error;

      await refetchWO();
      setShowPlybookSelector(false);
      setSelectedPlybook(null);
      setShowLaminationView(true);
    } catch (error) {
      console.error('Error linking plybook:', error);
    } finally {
      setIsLinking(false);
    }
  };

  const handleStartWithoutPlybook = () => {
    setShowLaminationView(true);
  };

  if (woLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show PlybookLaminationView if plybook is assigned
  if (showLaminationView && workOrder?.plybook_id) {
    return (
      <PlybookLaminationView
        workOrderId={workOrderId}
        plybookId={workOrder.plybook_id}
        onComplete={onComplete}
        onBack={() => setShowLaminationView(false)}
      />
    );
  }

  // Main selection view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          {onBack && (
            <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/10">
              ← Volver
            </Button>
          )}
          <h1 className="text-2xl font-bold">Preparar Laminado</h1>
          <div />
        </div>

        {/* Work Order Info Card */}
        <Card className="bg-white/10 border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Package className="w-5 h-5" />
                {workOrder?.order_number}
              </CardTitle>
              <Badge className="bg-blue-600">{workOrder?.product_name}</Badge>
            </div>
            {workOrder?.customer && (
              <CardDescription className="text-white/60">
                Cliente: {workOrder.customer}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Linked Plotter Cut */}
            {workOrder?.linked_plotter_cut && (
              <div className="p-3 rounded-lg bg-green-500/20 border border-green-500/30">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <Check className="w-4 h-4" />
                  <span className="font-medium">Paquete de Corte Vinculado</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-white/60">Código: </span>
                    <span className="text-white font-mono">{workOrder.linked_plotter_cut.package_code}</span>
                  </div>
                  <div>
                    <span className="text-white/60">Capas: </span>
                    <span className="text-white">{workOrder.linked_plotter_cut.total_layers}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-white/60">Nombre: </span>
                    <span className="text-white">{workOrder.linked_plotter_cut.package_name}</span>
                  </div>
                  {/* Materiales del paquete */}
                  {workOrder.linked_plotter_cut.materials && workOrder.linked_plotter_cut.materials.length > 0 && (
                    <div className="col-span-2 mt-2">
                      <span className="text-white/60 block mb-1">Materiales: </span>
                      <div className="flex flex-wrap gap-1">
                        {workOrder.linked_plotter_cut.materials.map((m: any, i: number) => (
                          <span key={i} className="px-2 py-0.5 bg-white/10 rounded text-xs text-white">
                            {m.raw_material_name || m.type} ({m.layers}x)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mold Info */}
            {workOrder?.mold && (
              <div className="p-3 rounded-lg bg-blue-500/20 border border-blue-500/30">
                <div className="flex items-center gap-2 text-blue-400">
                  <Check className="w-4 h-4" />
                  <span className="font-medium">Molde: {workOrder.mold.name}</span>
                </div>
              </div>
            )}

            {!workOrder?.linked_plotter_cut && (
              <div className="p-3 rounded-lg bg-amber-500/20 border border-amber-500/30">
                <div className="flex items-center gap-2 text-amber-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Sin paquete de corte vinculado</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plybook Section */}
        <Card className="bg-white/10 border-0">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Plybook de Laminado
            </CardTitle>
            <CardDescription className="text-white/60">
              Guía visual de capas y secuencia de laminado
            </CardDescription>
          </CardHeader>
          <CardContent>
            {workOrder?.plybook ? (
              // Has plybook assigned
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-lg bg-green-500/20 border border-green-500/30">
                  {workOrder.plybook.main_image_url ? (
                    <img
                      src={workOrder.plybook.main_image_url}
                      alt={workOrder.plybook.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-white/10 rounded-lg flex items-center justify-center">
                      <Layers className="w-8 h-8 text-white/40" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white">{workOrder.plybook.name}</h3>
                    {workOrder.plybook.description && (
                      <p className="text-white/60 text-sm mt-1">{workOrder.plybook.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <Badge className="bg-blue-600">
                        <Layers className="w-3 h-3 mr-1" />
                        {workOrder.plybook.total_plies} capas
                      </Badge>
                    </div>
                  </div>
                </div>

                <Button
                  size="lg"
                  onClick={() => setShowLaminationView(true)}
                  className="w-full h-14 text-lg bg-green-600 hover:bg-green-700 gap-2"
                >
                  <Layers className="w-6 h-6" />
                  INICIAR LAMINADO CON PLYBOOK
                  <ArrowRight className="w-5 h-5" />
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setShowPlybookSelector(true)}
                  className="w-full border-white/30 text-white hover:bg-white/10"
                >
                  Cambiar Plybook
                </Button>
              </div>
            ) : (
              // No plybook assigned
              <div className="space-y-4">
                <div className="p-6 rounded-lg bg-white/5 border-2 border-dashed border-white/20 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-white/40" />
                  <h3 className="text-lg font-medium text-white mb-2">Sin Plybook Asignado</h3>
                  <p className="text-white/60 text-sm mb-4">
                    Puedes buscar un plybook existente para guiar el laminado, o continuar sin guía visual.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => setShowPlybookSelector(true)}
                      className="bg-primary hover:bg-primary/90 gap-2"
                    >
                      <Search className="w-4 h-4" />
                      Buscar Plybook
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleStartWithoutPlybook}
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      Continuar sin Plybook
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Plybook Selector Dialog */}
      <Dialog open={showPlybookSelector} onOpenChange={setShowPlybookSelector}>
        <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Buscar Plybook
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Selecciona un plybook para guiar el proceso de laminado
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Buscar por nombre, modelo o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              />
            </div>

            {/* Plybooks List */}
            <ScrollArea className="h-[400px] pr-4">
              {plybooksLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : availablePlybooks.length === 0 ? (
                <div className="text-center py-8 text-white/60">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No se encontraron plybooks</p>
                  {searchTerm && <p className="text-sm mt-1">Intenta con otro término de búsqueda</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  {availablePlybooks.map((plybook) => (
                    <div
                      key={plybook.id}
                      onClick={() => setSelectedPlybook(plybook)}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all",
                        selectedPlybook?.id === plybook.id
                          ? "bg-primary/30 ring-2 ring-primary"
                          : "bg-white/5 hover:bg-white/10"
                      )}
                    >
                      {plybook.main_image_url ? (
                        <img
                          src={plybook.main_image_url}
                          alt={plybook.name}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-white/10 rounded flex items-center justify-center">
                          <Layers className="w-6 h-6 text-white/40" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white truncate">{plybook.name}</h4>
                        {plybook.description && (
                          <p className="text-sm text-white/60 line-clamp-1">{plybook.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-white/50">
                          <span className="flex items-center gap-1">
                            <Layers className="w-3 h-3" />
                            {plybook.total_plies} capas
                          </span>
                          {plybook.product_model && (
                            <span>Modelo: {plybook.product_model}</span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(plybook.created_at), 'dd/MM/yy', { locale: es })}
                          </span>
                        </div>
                      </div>
                      {selectedPlybook?.id === plybook.id && (
                        <Check className="w-5 h-5 text-primary shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowPlybookSelector(false);
                setSelectedPlybook(null);
              }}
              className="text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => selectedPlybook && handleLinkPlybook(selectedPlybook)}
              disabled={!selectedPlybook || isLinking}
              className="bg-primary hover:bg-primary/90"
            >
              {isLinking ? 'Vinculando...' : 'Usar este Plybook'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
